import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { distinctUntilChanged, EMPTY, Subject, switchMap } from "rxjs";
import { UserHeader } from "../../../../core/models/users/user-header";
import { Employee } from "../../../../core/models/profile/employee";
import { BillerCollectorService } from "../../../../core/services/biller-collector-service";
import { LoaderService } from "../../../../core/services/loader.service";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";
import { SpinnerComponent } from "../../../../shared/components/spinner/spinner.component";
import { Popover, PopoverModule } from "primeng/popover";
import { ScrollPanelModule } from "primeng/scrollpanel";
import { TableModule } from "primeng/table";
import { CommonModule } from "@angular/common";
import { SelectModule } from "primeng/select";
import { CardModule } from "primeng/card";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";

@UntilDestroy()
@Component({
  selector: "jni-add-edit-header",
  templateUrl: "./add-edit-header.component.html",
  styleUrls: ["./add-edit-header.component.scss"],
  imports: [
    SpinnerComponent,
    PopoverModule,
    ScrollPanelModule,
    TableModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SelectModule,
    CardModule,
    ButtonModule,
    InputTextModule,
  ],
})
export class AddEditHeaderComponent implements OnInit {
  @Input() set billerCollectorHeader(value: UserHeader) {
    this.billerCollectorForm?.patchValue({
      header: {
        userType: value.userType || "",
        personnelNumber: value.personnelNumber || "",
        statisticsGroup: value.statisticsGroup || "",
        name: value.name || "",
        phone: value.phone || "",
        email: value.email || "",
      },
    });
    this.pNumber = value.personnelNumber || "";
    this.personnelNumber = value.personnelNumber || "";
    this.onUserSelection(value.userType, true);
  }
  @Output() validationErrorEvent = new EventEmitter();
  @Output() smallLoaderEvent = new EventEmitter();
  @Output() refreshGrid = new EventEmitter();
  billerCollectorForm!: FormGroup;
  personnelNumber = "";
  pNumber = "";
  showSmallLoader = false;
  userList: UserHeader[] = [];
  searchList: Employee[] = [];
  billerCollectorList: Employee[] = [];
  isCollector = false;

  public searchUser$ = new Subject<string>();

  formErrors: Record<string, string> = {
    userType: "",
    personnelNumber: "",
    statisticsGroup: "",
    name: "",
    phone: "",
    email: "",
  };

  validationMessages: Record<string, Record<string, string>> = {
    userType: {
      required: "User Type is required.",
    },
    personnelNumber: {
      required: "Personal Number is required.",
    },
    statisticsGroup: {
      required: "Statistics Group is required.",
    },
    name: {
      required: "Name is required.",
      pattern: "Name format is not correct.",
      maxlength: "Name length should be less than 512 characters.",
    },
    phone: {
      required: "Phone is required.",
    },
    email: {
      required: "Email is required.",
      pattern: "Email format is not correct.",
    },
  };

  userTypeDefaultSelectionId = "Biller";
  typeList = [
    {
      id: 1,
      name: "Biller",
    },
    {
      id: 2,
      name: "Collector",
    },
  ];

  constructor(
    private _formBuilder: FormBuilder,
    private _billerCollectorService: BillerCollectorService,
    private _loaderService: LoaderService,
    private _toastr: JniToastrService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.searchUser();
    this.fetchUsersbyPersonalNumber();
  }

  private initializeForm() {
    this.billerCollectorForm = this._formBuilder.group({
      header: this._formBuilder.group({
        userType: ["Biller", Validators.required],
        personnelNumber: ["", Validators.required],
        statisticsGroup: [""],
        name: [
          "",
          [
            Validators.required,
            Validators.pattern("^[a-zA-Z0-9 ]+$"),
            Validators.maxLength(512),
          ],
        ],
        phone: ["", Validators.required],
        email: [
          "",
          [
            Validators.required,
            Validators.pattern(
              `^[a-zA-Z0-9._%+-]+(?:'[a-zA-Z0-9._%+-]+)?@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$`
            ),
          ],
        ],
      }),
    });
  }

  addBillerCollectorRedirect() {
    this.clearUserForm();
  }

  submitButtonlogValidationErrors(
    group: FormGroup = this.billerCollectorForm
  ): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.get(key);
      if (abstractControl instanceof FormGroup) {
        this.submitButtonlogValidationErrors(abstractControl);
      } else {
        this.formErrors[key] = "";
        if (abstractControl && !abstractControl.valid) {
          const messages = this.validationMessages[key];
          for (const errorKey in abstractControl.errors) {
            if (errorKey && messages) {
              this.formErrors[key] += messages[errorKey] + " ";
            }
          }
        }
      }
    });
  }

  userSave() {
    this.submitButtonlogValidationErrors();

    this._loaderService.setLoaderState(true);

    if (this.pNumber == "") {
      this._toastr.error("Error", "Invalid Personal Number.");
      this._loaderService.setLoaderState(false);
      return;
    } else {
      this.patchPersonnelNumber(this.pNumber);
    }

    const QuoteFormObject = this.billerCollectorForm.getRawValue();

    if (!this.billerCollectorForm.valid) {
      this._loaderService.setLoaderState(false);
      return;
    }

    if (this.isCollector) {
      this._billerCollectorService
        .createEditCollector(QuoteFormObject?.header)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this._loaderService.setLoaderState(false);
            if (res.data) {
              this._toastr.success(
                "Success",
                "Collector added/updated successfully"
              );
              this.clearUserForm();
              this.refreshGrid.emit();
              this.fetchUsersbyPersonalNumber();
            } else {
              this._toastr.error(
                "Error",
                res.message || "Error adding/updating Collector"
              );
            }
          },
          error: res => {
            this._loaderService.setLoaderState(false);
            this._toastr.error(
              "Error",
              res.message || "Error adding/updating Collector"
            );
          },
        });
    } else {
      this._billerCollectorService
        .createEditBiller(QuoteFormObject?.header)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this._loaderService.setLoaderState(false);
            if (res.data) {
              this._toastr.success(
                "Success",
                "Biller added/updated successfully"
              );
              this.clearUserForm();
              this.refreshGrid.emit();
              this.fetchUsersbyPersonalNumber();
            } else {
              this._toastr.error(
                "Error",
                res.message || "Error adding/updating Biller"
              );
            }
          },
          error: res => {
            this._loaderService.setLoaderState(false);
            this._toastr.error(
              "Error",
              res.message || "Error adding/updating Biller"
            );
          },
        });
    }
  }

  logValidationErrors() {
    this.validationErrorEvent.emit();
  }

  getLoaderStatus(status: boolean) {
    this.smallLoaderEvent.emit(status);
  }

  searchUser() {
    this.searchUser$
      .pipe(
        distinctUntilChanged(),
        switchMap((searchitem: string) => {
          this.searchList = [];
          this.searchList = this.billerCollectorList?.filter(x =>
            x?.personnelNumber?.includes(searchitem)
          );
          return EMPTY;
        })
      )
      .subscribe({
        next: () => {
          this.showSmallLoader = false;
        },
        error: () => {
          this.showSmallLoader = false;
        },
      });
  }

  onUserRowSelection(userSelectedRow: UserHeader, userToggle: Popover) {
    this.clearUserForm(true);
    this.pNumber = userSelectedRow.personnelNumber;

    this.patchValuesInUserForm(userSelectedRow);
    userToggle.hide();
  }

  getUserAccount(event: KeyboardEvent, mydrop: Popover) {
    this.showSmallLoader = true;
    if (event.which == 9) {
      this.searchList = this.billerCollectorList?.filter(
        x => x?.personnelNumber == (event.target as HTMLInputElement).value
      );

      if (this.searchList.length > 0) {
        this.patchValuesInUserForm(this.searchList[0]);
        this.pNumber = this.searchList[0].personnelNumber ?? "";
      } else {
        this.clearUserForm(true);
      }

      mydrop.hide();

      this.showSmallLoader = false;
    }
  }

  patchValuesInUserForm(userSelectedRow: Employee) {
    this.billerCollectorForm.patchValue({
      header: {
        personnelNumber: userSelectedRow.personnelNumber,
        name: userSelectedRow.firstName + " " + userSelectedRow.lastName,
        phone: userSelectedRow.displayPhoneNumber,
        email: userSelectedRow.displayEmailAs,
      },
    });
  }

  patchPersonnelNumber(pNumber: string) {
    this.personnelNumber = pNumber;
    this.pNumber = pNumber;

    this.billerCollectorForm.patchValue({
      header: {
        personnelNumber: pNumber,
      },
    });
  }

  clearUserForm(partial = false) {
    if (!partial) {
      this.billerCollectorForm?.patchValue({
        header: {
          userType: "Biller",
        },
      });
      this.isCollector = false;
    }
    this.billerCollectorForm?.patchValue({
      header: {
        personnelNumber: "",
        statisticsGroup: "",
        name: "",
        phone: "",
        email: "",
      },
    });
    this.personnelNumber = "";
    this.pNumber = "";
  }

  onUserSearchOpenToggle(event: Event, mydrop: Popover) {
    mydrop.toggle(event);
  }

  onUserSelection(name: string, isEdit = false) {
    if (name == "Collector") {
      this.isCollector = true;
      this.setStatisticsValidator();
    } else {
      this.isCollector = false;
      this.removeStatisticsValidator();
    }

    if (!isEdit) this.clearUserForm(true);

    this.fetchUsersbyPersonalNumber();
  }

  removeStatisticsValidator() {
    if (this.billerCollectorForm?.get(["header", "statisticsGroup"])) {
      const control = this.billerCollectorForm.get([
        "header",
        "statisticsGroup",
      ]);
      if (control) {
        control.clearValidators();
        control.setErrors(null);
        control.updateValueAndValidity();
      }
    }
  }

  setStatisticsValidator() {
    if (this.billerCollectorForm?.get(["header", "statisticsGroup"])) {
      this.billerCollectorForm
        .get(["header", "statisticsGroup"])
        ?.setValidators(Validators.required);
    }
  }

  fetchUsersbyPersonalNumber() {
    this._billerCollectorService
      .GetUserByPersonnelNumber(this.userTypeDefaultSelectionId)
      .subscribe({
        next: res => {
          this.showSmallLoader = false;
          this.billerCollectorList = res.data;
          this.searchList = res.data;
        },
        error: () => {
          this.showSmallLoader = false;
        },
      });
  }
}
